import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Avatar, LinearProgress, Alert, Tabs, Tab
} from '@mui/material';
import {
  LocationOn, MyLocation, DirectionsCar, CheckCircle, Timer, Map
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import VerificationMap from './VerificationMap';
import geofencingService from '../services/geofencingService';
import { calculateDistance } from '../utils/geo';

const VerifierLocationTracker = ({ verificationRequestId, verifierInfo, projectLocation }) => {
  const [verifierLocation, setVerifierLocation] = useState(null);
  const [distance, setDistance] = useState(null);
  const [status, setStatus] = useState('waiting'); // waiting, approaching, arrived, in_progress
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = info, 1 = map
  const [notifications, setNotifications] = useState([]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Setup notification listener
    geofencingService.onNotification((notification) => {
      setNotifications(prev => [...prev, { ...notification, id: Date.now(), timestamp: new Date() }]);
      
      // Auto-dismiss after 10 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }, 10000);
    });

    return () => {
      geofencingService.reset();
    };
  }, []);

  useEffect(() => {
    // Connect to Socket.io
    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:5000');
    
    // Join project room to receive updates
    socket.emit('join-project', verificationRequestId);
    
    // Listen for verifier location updates
    socket.on('verifier-location-update', (data) => {
      if (data.verificationRequestId === verificationRequestId) {
        console.log('Verifier location update:', data);
        setVerifierLocation(data.location);
        setLastUpdate(new Date(data.timestamp));
        
        // Calculate distance if we have project location
        if (projectLocation && data.location) {
          const dist = calculateDistance(data.location.lat, data.location.lng, projectLocation.lat, projectLocation.lng);
          setDistance(dist);
          
          // Check geofencing and trigger alerts
          const geofenceResult = geofencingService.checkGeofence(data.location, projectLocation);
          
          if (geofenceResult.notification) {
            geofencingService.triggerNotification(geofenceResult.notification);
          }
          
          // Update status based on distance
          if (dist < 0.1) { // Less than 100m
            setStatus('arrived');
          } else if (dist < 5) { // Less than 5km
            setStatus('approaching');
          } else {
            setStatus('on_way');
          }
        }
      }
    });
    
    // Listen for verification started
    socket.on('verification-started', (data) => {
      if (data.verificationRequestId === verificationRequestId) {
        setStatus('in_progress');
      }
    });
    
    // Listen for verification completed
    socket.on('verification-completed', (data) => {
      if (data.verificationRequestId === verificationRequestId) {
        setStatus('completed');
      }
    });
    
    return () => {
      socket.disconnect();
    };
  }, [verificationRequestId, projectLocation]);

  const getStatusColor = () => {
    switch (status) {
      case 'arrived': return 'success';
      case 'approaching': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'waiting': return 'Waiting for verifier...';
      case 'on_way': return 'Verifier is on the way';
      case 'approaching': return 'Verifier is approaching';
      case 'arrived': return 'Verifier has arrived!';
      case 'in_progress': return 'Verification in progress';
      case 'completed': return 'Verification completed';
      default: return 'Unknown status';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'arrived': return <CheckCircle />;
      case 'approaching':
      case 'on_way': return <DirectionsCar />;
      case 'in_progress': return <Timer />;
      case 'completed': return <CheckCircle />;
      default: return <LocationOn />;
    }
  };

  const formatDistance = (km) => {
    if (km < 1) {
      return `${Math.round(km * 1000)}m away`;
    }
    return `${km.toFixed(1)}km away`;
  };

  const getETA = (distanceKm) => {
    // Assume average speed of 30km/h in city
    const hours = distanceKm / 30;
    const minutes = Math.ceil(hours * 60);
    if (minutes < 1) return 'Arriving now';
    if (minutes === 1) return '1 minute';
    return `${minutes} minutes`;
  };

  return (
    <Box>
      {/* Geofencing Notifications */}
      {notifications.map(notif => (
        <Alert
          key={notif.id}
          severity={notif.severity}
          onClose={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))}
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2">{notif.title}</Typography>
          <Typography variant="body2">{notif.message}</Typography>
        </Alert>
      ))}

      <Card sx={{ bgcolor: status === 'arrived' ? 'success.light' : 'background.paper' }}>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: getStatusColor() + '.main' }}>
              {getStatusIcon()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">
                Live Verification Tracking
              </Typography>
              <Chip
                label={getStatusText()}
                color={getStatusColor()}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
            <Tab icon={<LocationOn />} label="Status" />
            <Tab icon={<Map />} label="Map" disabled={!verifierLocation} />
          </Tabs>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Box>
            {/* Verifier Info */}
            {verifierInfo && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Verifier: <strong>{verifierInfo.name}</strong>
                </Typography>
                {verifierInfo.phone && (
                  <Typography variant="body2" color="text.secondary">
                    Phone: {verifierInfo.phone}
                  </Typography>
                )}
              </Box>
            )}

        {/* Location Status */}
        {verifierLocation && distance !== null && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDistance(distance)}
              </Typography>
              {distance > 0.1 && (
                <Typography variant="caption" color="text.secondary">
                  ETA: {getETA(distance)}
                </Typography>
              )}
            </Box>
            
            {/* Distance Progress Bar */}
            {status !== 'completed' && distance > 0.1 && (
              <LinearProgress
                variant="determinate"
                value={Math.max(0, 100 - (distance * 10))}
                sx={{ height: 8, borderRadius: 4 }}
              />
            )}
          </Box>
        )}

        {/* GPS Coordinates (for debugging) */}
        {verifierLocation && (
          <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              <MyLocation fontSize="inherit" /> GPS: {verifierLocation.lat.toFixed(6)}, {verifierLocation.lng.toFixed(6)}
            </Typography>
            {lastUpdate && (
              <Typography variant="caption" color="text.secondary" display="block">
                Updated: {lastUpdate.toLocaleTimeString()}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              Accuracy: ±{Math.round(verifierLocation.accuracy)}m
            </Typography>
          </Box>
        )}

        {/* Status Messages */}
        {status === 'arrived' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Verifier has arrived at your property! Verification will begin shortly.
          </Alert>
        )}
        
        {status === 'in_progress' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Verification is now in progress. You'll be notified when it's complete.
          </Alert>
        )}
        
        {status === 'completed' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Verification completed! Check your dashboard for the report.
          </Alert>
        )}
        
            {!verifierLocation && status === 'waiting' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Waiting for verifier to start their journey...
              </Alert>
            )}
          </Box>
        )}

          {/* Map Tab */}
          {activeTab === 1 && projectLocation && verifierLocation && (
            <Box>
              <VerificationMap
                verifierLocation={verifierLocation}
                propertyLocation={projectLocation}
                verifierName={verifierInfo?.name || 'Verifier'}
                showRoute={true}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerifierLocationTracker;

